import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Users, Building2, TrendingUp, Activity, ShieldCheck } from "lucide-react";

const STATS = [
  {
    icon: Users,
    key: "homes",
    to: 3460,
    suffix: "+",
    sub: { es: ["Hogares conectados", "este año"], en: ["Homes connected", "this year"] },
  },
  {
    icon: Building2,
    key: "cities",
    to: 38,
    suffix: "",
    sub: { es: ["Ciudades activas", "en Texas"], en: ["Active cities", "in Texas"] },
  },
  {
    icon: Activity,
    key: "monthly",
    to: 215,
    suffix: "",
    sub: { es: ["Promedio de", "nuevos por mes"], en: ["Avg. new homes", "per month"] },
  },
  {
    icon: TrendingUp,
    key: "growth",
    to: 24,
    suffix: "%",
    sub: { es: ["Crecimiento", "año tras año"], en: ["Growth", "year over year"] },
  },
];

function useCountUp(target, durationMs = 1700, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return val;
}

function StatCard({ stat, idx, start, lang }) {
  const { t } = useTranslation();
  const value = useCountUp(stat.to, 1700, start);
  const subParts = stat.sub[lang] || stat.sub.es;
  const renderSub = (parts) =>
    parts.map((p, i) => {
      const isAccent = i === 1;
      return (
        <span key={i} className={isAccent ? "text-velcor-green font-bold" : ""}>
          {p}{i < parts.length - 1 ? " " : ""}
        </span>
      );
    });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={start ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.15 + idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col items-start"
      data-testid={`stat-${stat.key}`}
    >
      <span className="grid place-items-center w-14 h-14 rounded-full bg-velcor-green/10 ring-1 ring-velcor-green/25 mb-5 backdrop-blur-sm">
        <stat.icon className="w-5 h-5 text-velcor-green" strokeWidth={2.2} />
      </span>
      <div className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-600 mb-3 leading-tight">
        {t(`home.stat_${stat.key}_label`)}
      </div>
      <div
        className="display text-velcor-ink leading-none flex items-baseline gap-1"
        style={{ fontSize: "clamp(40px, 5vw, 60px)", fontWeight: 500, letterSpacing: "-0.035em" }}
      >
        {stat.prefix && (
          <span className="text-velcor-green/85" style={{ fontSize: "0.5em", marginRight: 1 }}>
            {stat.prefix}
          </span>
        )}
        <span>{value.toLocaleString("en-US")}</span>
        {stat.suffix && (
          <span className="display-accent text-velcor-green" style={{ fontSize: "0.55em" }}>
            {stat.suffix}
          </span>
        )}
      </div>
      <div className="mt-4 h-[2px] w-9 bg-velcor-green rounded-full" />
      <p className="mt-3 text-slate-600 leading-snug" style={{ fontSize: 13 }}>
        {renderSub(subParts)}
      </p>
    </motion.div>
  );
}

export default function StatCounters() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-y border-slate-200/70"
      data-testid="stat-counters"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfaf6 50%, #f6f5ee 100%)" }}
    >
      {/* Top + bottom hairlines */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-velcor-green/30 to-transparent z-20" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-velcor-green/20 to-transparent z-20" />

      {/* FULL-BLEED Texas map backdrop — using the user-provided MAPA TX.jpg */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, #000 18%, #000 78%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, #000 18%, #000 78%, transparent 100%)",
        }}
      >
        <motion.img
          src="/texas-map.jpg"
          alt=""
          loading="lazy"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover md:object-contain object-center"
          style={{ minHeight: "100%" }}
          data-testid="texas-map-img"
        />
      </div>

      {/* Soft cream tint over the map for overall harmony */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-[1]"
        style={{
          background:
            "radial-gradient(ellipse 120% 80% at 50% 50%, rgba(251,250,246,0.15) 0%, rgba(251,250,246,0.30) 100%)",
        }}
      />

      <div className="relative z-10 max-w-[1240px] mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Title — sits low in the section, with backdrop-blur for legibility */}
        <div className="pt-24 md:pt-32 lg:pt-36 pb-6 md:pb-8 max-w-[640px]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative inline-block"
          >
            {/* Backdrop blur halo behind the title */}
            <div
              className="absolute -inset-x-6 -inset-y-5 rounded-3xl"
              style={{
                background: "rgba(251, 250, 246, 0.55)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                boxShadow: "0 0 60px 30px rgba(251, 250, 246, 0.5)",
              }}
              aria-hidden
            />
            <div className="relative">
              <span
                className="inline-flex items-center gap-2 text-velcor-green mb-3"
                style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase" }}
              >
                <span className="block w-1.5 h-1.5 rounded-full bg-velcor-green" />
                {t("home.statKicker")}
              </span>
              <h2
                className="display text-velcor-ink leading-[1.04]"
                style={{ fontSize: "clamp(28px, 3.6vw, 46px)", fontWeight: 600, letterSpacing: "-0.02em" }}
                data-testid="stat-title"
              >
                {t("home.statTitlePre")}{" "}
                <span className="display-accent">{t("home.statTitleAccent")}</span>
              </h2>
              <div className="mt-4 h-[3px] w-12 bg-velcor-green rounded-full" />
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 pt-8">
          <div
            aria-hidden
            className="absolute -inset-x-6 -inset-y-5 rounded-3xl pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(251,250,246,0.55) 0%, rgba(251,250,246,0.85) 60%, rgba(251,250,246,0.95) 100%)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          />
          {STATS.map((s, i) => (
            <StatCard key={s.key} stat={s} idx={i} start={inView} lang={lang} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative mt-8 md:mt-10 flex items-center gap-2.5 text-slate-600"
          style={{ fontSize: 12.5 }}
          data-testid="stat-footer"
        >
          <ShieldCheck className="w-4 h-4 text-velcor-green shrink-0" strokeWidth={2.2} />
          <span>{t("home.statFooter")}</span>
        </motion.div>
      </div>
    </section>
  );
}

